import 'utils/moment-locale'
import React from 'react'
import { View } from 'react-native'
import { connect } from 'react-redux'
import DatePicker from 'react-datepicker'
import DatePickerMobile from '@protonapp/react-mobile-date-picker'
import PropTypes from 'prop-types'
import { bindingTypes, dataTypes } from '@adalo/constants'
import '@protonapp/react-mobile-date-picker/styles.css'

import { getDeviceType, DEVICE_TYPE_MOBILE } from 'utils/device'
import {
  fromDateOnlyStringToDateString,
  fromJSDateToStringStartOfDay,
  fromJSDateToStringStartOfMinute,
  getDateStringFromISO,
  toJSDate,
} from 'utils/luxon'
import { evaluateBinding } from '../../utils/dependencies'
import { isiOSBrowser } from '../../utils/browsers'

import { changeValue, setDefaultValue, getValue } from '../../ducks/formInputs'

import BaseObject from '../BaseObject'

import ActionWrapper from '../ActionWrapper'
import PortalContainer from './PortalContainer'

import 'react-datepicker/dist/react-datepicker.css'
import './DatePicker.css'

const defaults = {
  PLACEHOLDER_COLOR: '#9e9e9e',
}

class Picker extends BaseObject {
  static contextTypes = {
    getOffsetTop: PropTypes.func,
    getApp: PropTypes.func,
  }

  /**
   * Used by DatePickerMobile component
   *
   * @param {string} _dateString
   * @param {Date} dateObj
   */
  handleDateChange = (_dateString, dateObj) => {
    this.handleChange(dateObj)
  }

  /**
   * Used by DatePicker component
   *
   * @param {Date} newValue
   * @returns
   */
  handleChange = newValue => {
    if (Object.prototype.toString.call(newValue) === '[object Date]') {
      try {
        const { changeValue, object } = this.props

        changeValue(
          object.id,
          this.getShowTime()
            ? fromJSDateToStringStartOfMinute(newValue)
            : fromJSDateToStringStartOfDay(newValue)
        )
      } catch {
        // no-op
      }
    }
  }

  /**
   * Used by DatePicker component
   *
   * @param {Date} newValue
   */
  handleOnSelect = newValue => {
    const { value } = this.props

    if (!value) {
      this.handleChange(newValue)
    }
  }

  /**
   * Used by input[type="date"] (Date Text Input)
   *
   * @param {Event} e
   * @returns
   */
  handleChangeInput = e => {
    const newValue = e.target.value || null

    try {
      const { changeValue, object } = this.props

      changeValue(object.id, fromDateOnlyStringToDateString(newValue))
    } catch {
      // no-op
    }
  }

  hasDateBinding = (props = this.props) => {
    const { object } = props

    const hasDateDataBinding =
      object.dataBinding &&
      object.dataBinding.bindingType === bindingTypes.SET_DATE

    return hasDateDataBinding
  }

  getDefaultValue = (props = this.props) => {
    const { bindingData, defaultDate } = props

    if (defaultDate) {
      return defaultDate
    }

    if (this.hasDateBinding(props) && bindingData) {
      return bindingData
    }

    return ''
  }

  getDateString = () => {
    const { object } = this.props
    const { datePickerStyle } = object.attributes

    if (this.showMobile()) {
      if (datePickerStyle === 'dateTextInput') {
        return 'L'
      }

      return this.getShowTime() ? 'ddd, MMM DD, YYYY LT' : 'dddd, MMMM DD, YYYY'
    } else {
      if (datePickerStyle === 'dateTextInput') {
        return 'YYYY-MM-DD'
      }
      return this.getShowTime() ? 'E, LLL d, yyyy p' : 'EEEE, LLLL d, yyyy'
    }
  }

  /**
   *
   * @returns {boolean}
   */
  getShowTime = () => {
    const { object } = this.props
    const { datePickerStyle } = object.attributes

    return !datePickerStyle || datePickerStyle === 'date'
  }

  componentDidMount() {
    const { object, setDefaultValue } = this.props
    const defaultValue = this.getDefaultValue()

    setDefaultValue(object.id, defaultValue)
  }

  componentDidUpdate(prevProps) {
    const { object, bindingData, setDefaultValue, value } = this.props

    const defaultValue = this.getDefaultValue()

    if (value !== prevProps.value) {
      // check if date picker has a value, otherwise set a default value
      if (!value) {
        setDefaultValue(object.id, defaultValue)
      }
    }

    if (!value && !prevProps.active && this.props.active) {
      if (defaultValue) {
        setDefaultValue(object.id, defaultValue)
      }
    }

    if (
      this.hasDateBinding() &&
      bindingData &&
      bindingData !== prevProps.bindingData
    ) {
      setDefaultValue(object.id, bindingData)
    }
  }

  datePickerRef = el => {
    this._datePickerEl = el
  }

  showMobile = () => {
    const { app, deviceType } = this.props
    const { magicLayout, primaryPlatform } = app

    return (
      [null, 'mobile'].includes(primaryPlatform) ||
      (magicLayout && deviceType === DEVICE_TYPE_MOBILE)
    )
  }

  render() {
    const { object, component, bindingData, value } = this.props
    const { attributes, layout } = object

    const showTime = this.getShowTime()
    const dateString = this.getDateString()
    let mode = 'datetime'

    const initialDate = value
      ? fromJSDateToStringStartOfMinute(toJSDate(value))
      : fromJSDateToStringStartOfMinute(new Date())

    if (
      attributes.datePickerStyle === dataTypes.DATE_ONLY ||
      attributes.datePickerStyle === 'dateTextInput'
    ) {
      mode = 'date'
    }

    const bgStyles = this.backgroundStyles()
    const textStyles = this.textStyles()

    const isPlaceholder = value
      ? { color: `${textStyles.color}` }
      : { color: `${defaults.PLACEHOLDER_COLOR}` }

    // check if background color exists, otherwise default to white
    bgStyles.backgroundColor = bgStyles.backgroundColor || 'white'

    let dateValue

    if (this.showMobile()) {
      if (isiOSBrowser()) {
        dateValue = value && value.length > 0 ? initialDate : null
      } else {
        dateValue = value && value.length > 0 ? initialDate : undefined
      }

      return (
        <ActionWrapper
          style={[layout, this.getOpacity()]}
          component={component}
          object={object}
          bindingData={bindingData}
        >
          <DatePickerMobile
            style={{ width: '100%' }}
            date={dateValue}
            mode={mode}
            confirmBtnText="OK"
            cancelBtnText="Cancel"
            onDateChange={this.handleDateChange}
            showIcon={false}
            format={dateString}
            is24Hour={false}
            customStyles={{
              dateInput: {
                ...bgStyles,
                ...textStyles,
                ...this.borderStyles(),
                ...this.shadowStyles(),
                ...isPlaceholder,
                alignItems: 'left',
                paddingLeft: attributes.padding,
                paddingRight: attributes.padding,
                height: attributes.height,
              },
              btnTextText: {
                color: '#07f',
              },
            }}
          />
        </ActionWrapper>
      )
    } else {
      if (attributes.datePickerStyle === 'dateTextInput') {
        dateValue = value && value.length > 0 ? getDateStringFromISO(initialDate) : '' // prettier-ignore
      } else {
        dateValue = value && value.length > 0 ? new Date(initialDate) : new Date() // prettier-ignore
      }
    }

    if (attributes.datePickerStyle === 'dateTextInput') {
      const styles = {
        ...layout,
        ...this.borderStyles(),
        ...this.backgroundStyles(),
        ...this.getOpacity(),
        height: attributes.height,
      }

      const inputStyles = {
        ...textStyles,
        ...isPlaceholder,
        paddingLeft: attributes.padding,
      }

      return (
        <View style={styles}>
          <input
            type="date"
            className="date-text-input"
            style={inputStyles}
            value={dateValue || ''}
            onChange={this.handleChangeInput}
          />
        </View>
      )
    }

    return (
      <div
        ref={this.datePickerRef}
        style={{
          ...this.borderStyles(),
          ...this.backgroundStyles(),
          ...this.shadowStyles(),
          ...this.textStyles(),
          ...this.getOpacity(),
          ...layout,
          alignItems: 'flex-start',
          paddingLeft: attributes.padding,
          height: attributes.height,
          color: attributes.color,
        }}
      >
        <DatePicker
          className={`data-object-form-input-input ${
            !value && 'input-placeholder'
          }`}
          onSelect={this.handleOnSelect}
          selected={dateValue}
          onChange={this.handleChange}
          showTimeInput={showTime}
          dateFormat={dateString}
          popperContainer={PortalContainer}
        />
      </div>
    )
  }
}

const mapStateToProps = (state, props) => {
  const { app } = props

  return {
    value: getValue(state, props.object.id),
    defaultDate: evaluateBinding(
      state,
      props.object.attributes.defaultDate,
      props
    ),
    deviceType: getDeviceType(undefined, app),
  }
}

export default connect(mapStateToProps, { changeValue, setDefaultValue })(
  Picker
)
